import { getSystemName } from '@app/consts/common_consts';
import { checkEmptyValue, isNotEmpty } from '@app/utils/StringUtils';
import { type RouterProvider } from 'react-router-dom';
import { RouterAccessPermission } from '../common-route';
import { RewriteUrlPathRoute } from '../types';
import buildRouterItem from './build-route-item';

/**
 * Rewrites a given URL by extracting the screenId and prefixing it with a system path.
 *
 * @param {string} uri - The original URI to be rewritten.
 * @returns {RewriteUrlPathRoute} - The rewritten URL with the system path and screenId.
 */
const rewriteUrl = (uri: string): RewriteUrlPathRoute => {
  // Remove the file extension and split the URI into directory components
  const dirs = uri.replace(/\.(tsx|ts)$/, '').split('/');
  const [, , , _pages, permission, screenId] = dirs;

  return { resolvedPath: `/${screenId}`, permission };
};

const pageUrlLinks = import.meta.glob<AppModule>(
  ['../../../pages/public/**/route.(tsx|ts)', '../../../pages/private/**/route.(tsx|ts)'],
  { eager: false }
);

/**
 * Creates a list of routes based on the page modules.
 *
 * @param {Record<string, () => Promise<AppModule>>} pages - The page modules.
 * @returns {Promise<AppRoute[]>} - The list of routes.
 */
const createRouteLists = async (pages: Record<string, () => Promise<AppModule>>): Promise<AppRoute[]> => {
  /**
   * Iterate over the page modules and create a route for each one.
   *
   * @param {AppRoute[]} routes - The list of routes.
   * @param {[string, () => Promise<AppModule>]} [page, moduleLoader] - The page module loader.
   * @returns {Promise<AppRoute[]>} - The list of routes.
   */
  const routes: AppRoute[] = [];
  for (const [page, moduleLoader] of Object.entries(pages)) {
    const module = await moduleLoader();
    if (module) {
      /**
       * Get the route definition from the module.
       */
      const route = module.default;
      /**
       * Resolve the path of the route.
       */
      const { resolvedPath, permission } = rewriteUrl(page);

      /**
       * Set the path, title, screenId, and filePath of the route if they are not set.
       */
      route.path ??= resolvedPath;

      // Create a dynamic title getter that responds to language changes
      const baseTitle = route?.title;
      Object.defineProperty(route, 'title', {
        get() {
          const systemName = getSystemName();
          return baseTitle ? checkEmptyValue(baseTitle).concat(' | ', systemName) : systemName;
        },
        enumerable: true,
        configurable: true,
      });

      route.screenId ??= route.path;
      route.filePath = resolvedPath.replace(/\route\.(tsx|ts)/, '');
      // Prefer permission explicitly set in route.tsx, fallback to folder-based permission
      route.permission = route.permission ?? permission ?? RouterAccessPermission.Private;

      /**
       * Add the route to the list of routes.
       */
      routes.push(route);
    }
  }
  return routes;
};

/**
 * Creates the initial routes for the application.
 *
 * @returns {Promise<AppRoute[]>} - A promise that resolves with the list of routes.
 */
const initialRoutes = async (): Promise<RouterProvider> => {
  const routes: AppRoute[] = await createRouteLists(pageUrlLinks);
  const routesFiltered = routes.filter(({ screenId, path }) => isNotEmpty(screenId) && isNotEmpty(path));
  return buildRouterItem(routesFiltered);
};

export default initialRoutes;
